<script>
	import { SITE } from '$lib/config.js';
	import { getEvents, getArticles } from '$lib/api.js';
	import { getActiveSchedule } from '$lib/data/special-schedules.js';
	import EventCard from '$lib/components/EventCard.svelte';
	import ArchiveArticleCard from '$lib/components/ArchiveArticleCard.svelte';
	import LoadingSpinner from '$lib/components/LoadingSpinner.svelte';
	import ContentError from '$lib/components/ContentError.svelte';
	import SpecialSchedule from '$lib/components/SpecialSchedule.svelte';

	const activeSchedule = getActiveSchedule();

	const STALENESS_DAYS = 90;

	let events = $state([]);
	let articles = $state([]);
	let eventsLoading = $state(true);
	let articlesLoading = $state(true);
	let eventsError = $state(null);
	let articlesError = $state(null);

	function todayStr() {
		return new Date().toISOString().slice(0, 10);
	}

	const upcomingEvents = $derived(
		events.filter(e => e.date >= todayStr()).sort((a, b) => a.date.localeCompare(b.date))
	);

	const recentArticles = $derived(() => {
		const cutoff = new Date();
		cutoff.setDate(cutoff.getDate() - STALENESS_DAYS);
		const cutoffStr = cutoff.toISOString().slice(0, 10);
		return [...articles]
			.filter(a => a.date >= cutoffStr)
			.sort((a, b) => b.date.localeCompare(a.date))
			.slice(0, 3);
	});

	$effect(() => {
		getEvents(20)
			.then(data => { events = data.items; })
			.catch(err => { eventsError = err; })
			.finally(() => { eventsLoading = false; });

		getArticles({ limit: 20 })
			.then(data => { articles = data.items; })
			.catch(err => { articlesError = err; })
			.finally(() => { articlesLoading = false; });
	});
</script>

<svelte:head>
	<title>St. Thomas Armenian Apostolic Church — Tenafly, NJ</title>
	<meta name="description" content="St. Thomas Armenian Apostolic Church in Tenafly, NJ — a parish of faith, heritage, and community serving Bergen County's Armenian community since 1957." />
</svelte:head>

<!-- 1. Hero -->
<section class="relative">
	<img
		src="/images/DSCN1591.webp"
		alt="St. Thomas Armenian Church sanctuary"
		class="w-full object-cover aspect-[3/2]"
	/>
	<div class="absolute inset-0 flex items-center justify-center">
		<h1 class="rounded-lg bg-navy/50 px-8 py-4 font-heading text-4xl font-bold text-white backdrop-blur-sm md:text-6xl">
			St. Thomas Armenian Apostolic Church
		</h1>
	</div>
</section>

<!-- 2. Service Times -->
{#if activeSchedule}
	<SpecialSchedule schedule={activeSchedule} />
{:else}
	<section class="bg-parchment-dark py-10 md:py-12">
		<div class="section-container">
			<div class="mx-auto max-w-4xl">
				<h2 class="text-center font-heading text-2xl font-bold text-navy md:text-3xl">Service Times</h2>
				<div class="mt-6 grid gap-6 sm:grid-cols-2">
					<div class="rounded-lg border border-stone-dark bg-stone p-6 text-center">
						<h3 class="font-heading text-lg font-semibold text-navy">Morning Service</h3>
						<p class="mt-2 font-body text-2xl text-navy-light">9:30 AM</p>
						<p class="mt-1 text-sm text-navy-muted">Every Sunday</p>
					</div>
					<div class="rounded-lg border border-stone-dark bg-stone p-6 text-center">
						<h3 class="font-heading text-lg font-semibold text-navy">Divine Liturgy (Soorp Badarak)</h3>
						<p class="mt-2 font-body text-2xl text-navy-light">10:30 AM</p>
						<p class="mt-1 text-sm text-navy-muted">Every Sunday</p>
					</div>
				</div>
			</div>
		</div>
	</section>
{/if}

<!-- 3. CTA Row -->
<section class="py-10 md:py-12">
	<div class="section-container">
		<div class="mx-auto max-w-4xl">
			<div class="grid gap-6 sm:grid-cols-3">
				<a
					href={SITE.donateUrl}
					target="_blank"
					rel="noopener noreferrer"
					class="flex flex-col items-center rounded-lg border border-stone-dark bg-stone p-6 text-center transition-shadow hover:shadow-md"
				>
					<span class="font-heading text-lg font-semibold text-navy">Make a Donation</span>
					<span class="mt-2 text-sm text-navy-muted">Support our parish</span>
				</a>
				<a
					href="/watch"
					class="flex flex-col items-center rounded-lg border border-stone-dark bg-stone p-6 text-center transition-shadow hover:shadow-md"
				>
					<span class="font-heading text-lg font-semibold text-navy">Watch Live</span>
					<span class="mt-2 text-sm text-navy-muted">Sunday services on YouTube</span>
				</a>
				<a
					href={SITE.bulletinSignup}
					target="_blank"
					rel="noopener noreferrer"
					class="flex flex-col items-center rounded-lg border border-stone-dark bg-stone p-6 text-center transition-shadow hover:shadow-md"
				>
					<span class="font-heading text-lg font-semibold text-navy">Subscribe to Bulletin</span>
					<span class="mt-2 text-sm text-navy-muted">Stay connected</span>
				</a>
			</div>
		</div>
	</div>
</section>

<!-- 4. Upcoming Events (hidden when no upcoming events) -->
{#if eventsLoading}
	<section class="py-10 md:py-14">
		<div class="section-container">
			<h2 class="text-center font-heading text-3xl font-semibold text-navy">Upcoming Events</h2>
			<div class="divider-gold mt-4"></div>
			<LoadingSpinner />
		</div>
	</section>
{:else if eventsError}
	<section class="py-10 md:py-14">
		<div class="section-container">
			<h2 class="text-center font-heading text-3xl font-semibold text-navy">Upcoming Events</h2>
			<div class="divider-gold mt-4"></div>
			<ContentError message="We couldn't load upcoming events right now." />
		</div>
	</section>
{:else if upcomingEvents.length > 0}
	<section class="py-10 md:py-14">
		<div class="section-container">
			<h2 class="text-center font-heading text-3xl font-semibold text-navy">Upcoming Events</h2>
			<div class="divider-gold mt-4"></div>
			<div class="mt-8 flex flex-wrap justify-center gap-6">
				{#each upcomingEvents.slice(0, 4) as event (event.id)}
					<div class="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(25%-1.125rem)]">
						<EventCard {event} />
					</div>
				{/each}
			</div>
			<div class="mt-8 text-center">
				<a href="/events/" class="font-medium text-wine hover:text-wine-dark">View All Events →</a>
			</div>
		</div>
	</section>
{/if}

<!-- 5. News & Announcements (hidden when no recent articles) -->
{#if articlesLoading}
	<section class="bg-parchment-dark py-10 md:py-14">
		<div class="section-container">
			<h2 class="text-center font-heading text-2xl font-semibold text-navy md:text-3xl">News & Announcements</h2>
			<div class="divider-gold mt-4"></div>
			<LoadingSpinner />
		</div>
	</section>
{:else if recentArticles().length > 0}
	<section class="bg-parchment-dark py-10 md:py-14">
		<div class="section-container">
			<h2 class="text-center font-heading text-2xl font-semibold text-navy md:text-3xl">News & Announcements</h2>
			<div class="divider-gold mt-4"></div>
			<div class="mx-auto mt-6 flex max-w-3xl flex-col gap-3">
				{#each recentArticles() as article (article.id)}
					<ArchiveArticleCard {article} />
				{/each}
			</div>
			<div class="mt-6 text-center">
				<a href="/articles/" class="font-medium text-wine hover:text-wine-dark">View All News & Announcements →</a>
			</div>
		</div>
	</section>
{/if}
